import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Save, Trash2, Plus, X, DoorOpen, Maximize2 } from "lucide-react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { ROOM_STATUS_LABELS } from "@/components/property/roomMeta";
import { planStatus, ELEMENT_PALETTE, ELEMENT_META } from "@/components/property/floorPlanMeta";

const CANVAS_W = 1400;
const CANVAS_H = 900;
const GRID = 20;
const ROOM_W = 60;
const ROOM_H = 40;

const snap = (v) => Math.round(v / GRID) * GRID;

let _k = 0;
const newKey = () => `el_${Date.now()}_${_k++}`;

function ElementView({ el, selected, onPointerDown, onResizeDown, onDelete }) {
  const isRoom = el.kind === "room";
  const meta = !isRoom ? ELEMENT_META[el.kind] : null;
  const Icon = meta?.icon || DoorOpen;
  const st = isRoom ? planStatus(el._status) : null;
  return (
    <div
      data-testid={`plan-item-${el.key}`}
      onPointerDown={(e) => onPointerDown(e, el.key)}
      style={{ left: el.x, top: el.y, width: el.width, height: el.height, transform: el.rotation ? `rotate(${el.rotation}deg)` : undefined }}
      className={`absolute select-none touch-none cursor-move rounded-lg border-2 ${selected ? "ring-2 ring-primary z-20" : "z-10"} ${
        isRoom ? `${st.box} shadow-sm` : "bg-secondary/60 border-dashed border-border"
      }`}
    >
      {isRoom ? (
        <div className="w-full h-full grid place-items-center overflow-hidden px-1">
          <span className="text-xs font-semibold truncate" data-testid={`plan-room-number-${el.room_id}`}>{el._roomNumber || "Kamar"}</span>
        </div>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center gap-0.5 text-muted-foreground">
          <Icon size={16} />
          <span className="text-[10px] font-medium">{meta?.label}</span>
        </div>
      )}

      {selected && (
        <>
          <button type="button" data-testid={`plan-delete-${el.key}`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onDelete(el.key); }}
            className="absolute -top-2.5 -right-2.5 w-6 h-6 grid place-items-center rounded-full bg-destructive text-white shadow z-30">
            <X size={13} />
          </button>
          <div
            data-testid={`plan-resize-${el.key}`}
            onPointerDown={(e) => onResizeDown(e, el.key)}
            className="absolute -bottom-1.5 -right-1.5 w-4 h-4 rounded-sm bg-primary cursor-se-resize z-30"
          />
        </>
      )}
    </div>
  );
}

export default function FloorPlanEditor({ property, rooms = [], onClose }) {
  const propertyId = property?.id;
  const [elements, setElements] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const drag = useRef(null);
  const canvasRef = useRef(null);

  const roomMap = useRef({});
  useEffect(() => {
    roomMap.current = rooms.reduce((a, r) => { a[r.id] = r; return a; }, {});
  }, [rooms]);

  const decorate = useCallback((els) => els.map((e) => {
    if (e.kind !== "room") return e;
    const r = roomMap.current[e.room_id];
    return { ...e, _roomNumber: r?.room_number, _status: r?.status };
  }), []);

  useEffect(() => {
    if (!propertyId) return;
    setLoading(true);
    apiClient.get(`/properties/${propertyId}/floorplan`, { params: { floor: 1 } })
      .then((r) => setElements(decorate((r.data?.elements || []).map((e) => ({ ...e, key: e.key || newKey() })))))
      .catch(() => setElements([]))
      .finally(() => setLoading(false));
  }, [propertyId, decorate]);

  const placedRoomIds = new Set(elements.filter((e) => e.kind === "room").map((e) => e.room_id));
  const availableRooms = rooms.filter((r) => !placedRoomIds.has(r.id));

  const addRoom = (room) => {
    const n = elements.filter((e) => e.kind === "room").length;
    setElements((els) => [...els, {
      key: newKey(), kind: "room", room_id: room.id,
      x: snap(40 + (n % 6) * (ROOM_W + 20)), y: snap(40 + Math.floor(n / 6) * (ROOM_H + 40)),
      width: ROOM_W, height: ROOM_H, rotation: 0,
      _roomNumber: room.room_number, _status: room.status,
    }]);
  };

  const addElement = (kind) => {
    const meta = ELEMENT_META[kind];
    setElements((els) => [...els, {
      key: newKey(), kind, room_id: null,
      x: 60, y: 60, width: snap(meta.w), height: snap(meta.h), rotation: 0,
    }]);
  };

  const removeElement = (key) => {
    setElements((els) => els.filter((e) => e.key !== key));
    setSelected((s) => (s === key ? null : s));
  };

  const onPointerDown = (e, key) => {
    setSelected(key);
    const el = elements.find((x) => x.key === key);
    if (!el) return;
    drag.current = { key, mode: "move", startX: e.clientX, startY: e.clientY, origX: el.x, origY: el.y };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onResizeDown = (e, key) => {
    e.stopPropagation();
    setSelected(key);
    const el = elements.find((x) => x.key === key);
    if (!el) return;
    drag.current = { key, mode: "resize", startX: e.clientX, startY: e.clientY, origW: el.width, origH: el.height };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    setElements((els) => els.map((el) => {
      if (el.key !== d.key) return el;
      if (d.mode === "move") {
        const nx = snap(Math.max(0, Math.min(CANVAS_W - el.width, d.origX + dx)));
        const ny = snap(Math.max(0, Math.min(CANVAS_H - el.height, d.origY + dy)));
        return { ...el, x: nx, y: ny };
      }
      return { ...el, width: Math.max(GRID * 2, snap(d.origW + dx)), height: Math.max(GRID, snap(d.origH + dy)) };
    }));
  };

  const onPointerUp = () => { drag.current = null; };

  // Samakan ukuran semua kamar dalam satu klik (pakai ukuran kamar terpilih bila ada,
  // jika tidak pakai ukuran default) agar owner tak perlu resize satu per satu.
  const applyUniformRoomSize = () => {
    const sel = elements.find((e) => e.key === selected && e.kind === "room");
    const w = snap(sel?.width || ROOM_W);
    const h = snap(sel?.height || ROOM_H);
    setElements((els) => els.map((e) => (e.kind === "room" ? { ...e, width: w, height: h } : e)));
    toast.success(`Semua kamar disamakan ke ${w}×${h}px`);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        floor: 1,
        elements: elements.map(({ key, kind, room_id, x, y, width, height, rotation }) => ({
          key, kind, room_id: room_id || null, x, y, width, height, rotation: rotation || 0,
        })),
      };
      await apiClient.put(`/properties/${propertyId}/floorplan`, payload);
      toast.success("Denah kamar tersimpan");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gagal menyimpan denah");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="floorplan-editor" className="flex flex-col lg:flex-row gap-4">
      {/* Palette */}
      <div className="lg:w-64 shrink-0 space-y-4">
        <div>
          <p className="text-sm font-semibold mb-2">Tambah Kamar ke Denah</p>
          {availableRooms.length === 0 ? (
            <p className="text-xs text-muted-foreground">Semua kamar sudah ditempatkan.</p>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="floorplan-room-palette">
              {availableRooms.map((r, i) => {
                const st = planStatus(r.status);
                return (
                  <button key={r.id} type="button" onClick={() => addRoom(r)} data-testid={`floorplan-room-option-${i}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border text-xs font-semibold hover:bg-secondary/60 transition-colors">
                    <span className={`w-2.5 h-2.5 rounded-full ${st.dot}`} /> {r.room_number}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div>
          <p className="text-sm font-semibold mb-2">Elemen Denah</p>
          <div className="flex flex-wrap gap-2" data-testid="floorplan-element-palette">
            {ELEMENT_PALETTE.map((el) => (
              <button key={el.kind} type="button" onClick={() => addElement(el.kind)} data-testid={`floorplan-element-${el.kind}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border text-xs font-semibold hover:bg-secondary/60 transition-colors">
                <el.icon size={13} /> {el.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-sm font-semibold mb-2">Ukuran Kamar</p>
          <button type="button" onClick={applyUniformRoomSize} data-testid="floorplan-uniform-size"
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full border border-border text-xs font-semibold hover:bg-secondary/60 transition-colors">
            <Maximize2 size={13} /> Samakan Ukuran Semua Kamar
          </button>
          <p className="mt-1 text-[11px] text-muted-foreground">Pilih 1 kamar sebagai acuan ukuran lalu klik. Tanpa pilihan, memakai ukuran default.</p>
        </div>
        <div className="rounded-xl border border-border bg-secondary/20 p-3 text-xs text-muted-foreground space-y-1">
          <p className="font-semibold text-foreground">Legenda Status</p>
          <p><span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500 mr-1.5" /> Tersedia</p>
          <p><span className="inline-block w-2.5 h-2.5 rounded-full bg-rose-500 mr-1.5" /> Terisi</p>
          <p><span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-400 mr-1.5" /> Akan tersedia</p>
          <p><span className="inline-block w-2.5 h-2.5 rounded-full bg-gray-400 mr-1.5" /> Tidak tersedia</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={save} disabled={saving} data-testid="floorplan-save"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:scale-105 transition-transform disabled:opacity-60">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Simpan Denah
          </button>
          {onClose && (
            <button type="button" onClick={onClose} data-testid="floorplan-close"
              className="px-4 py-2.5 rounded-full border border-border text-sm font-semibold hover:bg-secondary/60 transition-colors">
              Tutup
            </button>
          )}
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold mb-2">Denah Lantai 1</p>
        {loading ? (
          <div className="h-[60vh] grid place-items-center rounded-2xl border border-border"><Loader2 className="animate-spin text-primary" size={24} /></div>
        ) : (
          <div className="overflow-auto rounded-2xl border border-border bg-[linear-gradient(to_right,rgba(0,0,0,0.07)_1px,transparent_1px),linear-gradient(to_bottom,rgba(0,0,0,0.07)_1px,transparent_1px)] [background-size:20px_20px]" style={{ maxHeight: "60vh" }}>
            <div
              ref={canvasRef}
              data-testid="floorplan-canvas"
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerLeave={onPointerUp}
              onPointerDown={(e) => { if (e.target === canvasRef.current) setSelected(null); }}
              className="relative"
              style={{ width: CANVAS_W, height: CANVAS_H }}
            >
              {elements.map((el) => (
                <ElementView key={el.key} el={el} selected={selected === el.key}
                  onPointerDown={onPointerDown} onResizeDown={onResizeDown} onDelete={removeElement} />
              ))}
              {elements.length === 0 && (
                <div className="absolute inset-0 grid place-items-center pointer-events-none">
                  <p className="text-sm text-muted-foreground">Tambahkan kamar atau elemen dari panel kiri, lalu geser untuk mengatur posisi.</p>
                </div>
              )}
            </div>
          </div>
        )}
        <p className="mt-2 text-xs text-muted-foreground">Tips: geser kartu untuk memindahkan, tarik pojok kanan-bawah untuk mengubah ukuran, klik kartu lalu tombol × untuk menghapus dari denah (data kamar tidak terhapus).</p>
      </div>
    </div>
  );
}
